import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { CircularProgress, Box, Typography, Button } from '@mui/material';

interface AdminRouteProps {
  children: React.ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps) {
  const { session, loading: authLoading } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function checkAdminStatus() {
      // Don't check admin status until auth is complete
      if (authLoading) {
        return;
      }

      console.log('Current session:', session);
      console.log('Session email:', session?.user?.email);
      console.log('Checking admin status:', { session, authLoading });
      
      if (!session) {
        if (isMounted) {
          console.log('No session found, setting isAdmin to false');
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }

      try {
        console.log('Calling is_admin RPC');
        // Add a timeout to handle unresponsive RPC calls
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Admin check timed out')), 5000)
        );
        
        // Race between the actual RPC call and the timeout
        const result = await Promise.race([
          supabase.rpc('is_admin'),
          timeoutPromise
        ]) as { data: boolean | null, error: any };
        
        console.log('is_admin response:', result);
        
        if (result.error) {
          console.error('Error checking admin status:', result.error);
          
          // Try fallback admin check using user email
          console.log('Trying fallback admin check');
          
          // Get hardcoded admin emails from environment
          const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || ['admin@repacked.nl', 'leon@repacked.nl'];
          const isAdminByEmail = session?.user?.email && adminEmails.includes(session.user.email.toLowerCase());
          
          console.log('Admin check by email:', { email: session?.user?.email, isAdmin: isAdminByEmail });
          
          if (isAdminByEmail) {
            console.log('User is admin based on email');
            if (isMounted) {
              setIsAdmin(true);
              setError(null);
            }
          } else {
            if (isMounted) {
              setError(`Admin check failed: ${result.error.message || 'Unknown error'}`);
              setIsAdmin(false);
            }
          }
        } else {
          console.log('Setting isAdmin to:', !!result.data);
          if (isMounted) {
            setIsAdmin(!!result.data);
            setError(null);
          }
        }
      } catch (err: any) {
        console.error('Exception checking admin status:', err);
        
        // Try fallback admin check using user email
        console.log('Trying fallback admin check after exception');
        
        // Get hardcoded admin emails from environment
        const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || ['admin@repacked.nl', 'leon@repacked.nl'];
        const isAdminByEmail = session?.user?.email && adminEmails.includes(session.user.email.toLowerCase());
        
        console.log('Admin check by email:', { email: session?.user?.email, isAdmin: isAdminByEmail });
        
        if (isAdminByEmail) {
          console.log('User is admin based on email');
          if (isMounted) {
            setIsAdmin(true);
            setError(null);
          }
        } else {
          if (isMounted) {
            setError(`Admin check failed: ${err.message || 'Unknown error'}`);
            setIsAdmin(false);
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    setLoading(true);
    checkAdminStatus();

    return () => {
      isMounted = false;
    };
  }, [session, authLoading]); // Add authLoading to dependencies

  console.log('AdminRoute render:', { authLoading, loading, isAdmin, error, path: location.pathname });

  // Show loading state while we're checking authentication or admin status
  if (authLoading || loading || isAdmin === null) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Checking admin permissions...
        </Typography>
      </Box>
    );
  }

  // If there was an error checking admin status, show error message with retry button
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Error Accessing Admin Panel
        </Typography>
        <Typography variant="body1" align="center" paragraph>
          {error}
        </Typography>
        <Typography variant="body2" align="center" paragraph>
          This could be due to a temporary issue with the server or insufficient permissions.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
        <Button 
          variant="text" 
          sx={{ mt: 1 }}
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </Button>
      </Box>
    );
  }

  // Only redirect if we're sure about the session state
  if (!session) {
    console.log('No session, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only redirect if we're sure about the admin state
  if (isAdmin === false) {
    console.log('Not admin, redirecting to home');
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  console.log('Rendering admin content');
  return <>{children}</>;
} 